import React from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { Controller, SubmitHandler, useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import ErrorIcon from '@material-ui/icons/Error';
import useModal from '@yaydoo/react-components/lib/hooks/useModal';

import useStore from '@hooks/useStore';
import Input from '@components/common/Input';
import MaskedInput from '@components/common/MaskedInput';
import ValidateOtpForCard from '@components/modals/ValidateOtpForCard';
import PayButton from '@components/PayButton';
import { IKushkiPaymentData, PartnerFormProps } from '@containers/checkout/payment-form/types';
import { isCardNumberValid, isCVVNumberValid, isValidExpirationDate } from '@utils/validators';
import useStyles from './styles';
import { FieldValues } from 'react-hook-form/dist/types';
import Redirect3dSecure from '@components/modals/Redirect3dSecure';
import { Partner } from '@constants/paymentLink';
import FormPartnerLogo from '@components/FormPartnerLogo';
import { getCardType } from '@utils/openPay';
import { getCardIcon } from '@utils/cardHelper';

const KushkiCardForm: React.FC<PartnerFormProps> = ({ onValidationError, apiKey }) => {
  const css = useStyles();
  const {
    kushki,
    checkout,
    links: {
      paymentLink: { paymentLinkId, total: amount, isSubscription },
    },
  } = useStore();
  const { t } = useTranslation();
  const { showModal, closeModal } = useModal();
  const { control, formState, getValues, handleSubmit, watch } = useFormContext();
  const cardProvider = getCardType(watch('cardNumber') || '');
  const CardProviderIcon = getCardIcon(cardProvider);

  React.useEffect(() => {
    if (kushki.flags.isEnteringCodeForPreauth) {
      const data = { ...getValues(), paymentLinkId };
      showModal({
        component: ValidateOtpForCard,
        props: { data, apiKey },
      });
    } else {
      closeModal();
    }
  }, [kushki.flags.isEnteringCodeForPreauth]);

  React.useEffect(() => {
    if (kushki.flags.is3DSPreauth) {
      showModal({
        component: Redirect3dSecure,
        props: { callback: () => console.log('asd'), amount },
      });
    }

    if (!kushki.flags.is3DSPreauth) {
      closeModal();
    }
  }, [kushki.flags.is3DSPreauth]);

  const CardPaymentError: React.FC = () => (
    <Grid item className={css.paymentError}>
      <ErrorIcon />{' '}
      {kushki?.errors?.card?.message !== ''
        ? kushki?.errors?.card?.message
        : t('checkoutScreen.payment.errors.card.unknown')}
    </Grid>
  );

  const pay = (values: IKushkiPaymentData) => {
    const data: IKushkiPaymentData = {
      ...values,
      paymentLinkId,
      amount: +amount,
    };
    kushki.processCardPayment(data as IKushkiPaymentData, apiKey);
  };

  return (
    <>
      <Grid container spacing={2}>
        {kushki.hasCardProcessErrors && <CardPaymentError />}
        <Grid item xs={12}>
          <Controller
            name="cardHolderName"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                data-testid="cardholder-name"
                fullWidth
                autoComplete="off"
                label={t('checkoutScreen.payment.methods.card.name')}
                disabled={formState.isSubmitting}
                error={fieldState.invalid || kushki.hasCardProcessErrors}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="cardNumber"
            control={control}
            defaultValue={false}
            rules={{ required: true, validate: { card: isCardNumberValid } }}
            render={({ field, fieldState }) => (
              <MaskedInput
                fullWidth
                data-testid="payment-number"
                label={t('checkoutScreen.payment.methods.card.number')}
                autoComplete="off"
                inputProps={{
                  inputMode: 'numeric',
                  guide: true,
                  mask: '9999 9999 9999 9999',
                }}
                onChange={(e) => {
                  field.onChange(e.target.value.replace(/\s/g, ''));
                }}
                disabled={formState.isSubmitting}
                error={fieldState.invalid || kushki.hasCardProcessErrors}
                InputProps={{
                  endAdornment: CardProviderIcon && <CardProviderIcon />,
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="cardExpiration"
            control={control}
            defaultValue={false}
            rules={{
              required: true,
              validate: { expiration: isValidExpirationDate },
            }}
            render={({ field, fieldState }) => (
              <MaskedInput
                fullWidth
                data-testid="payment-card-expires"
                label={t('checkoutScreen.payment.methods.card.validity')}
                autoComplete="off"
                placeholder={t('checkoutScreen.payment.methods.card.validityPlaceholder')}
                inputProps={{
                  inputMode: 'numeric',
                  guide: true,
                  mask: '99/99',
                }}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                disabled={formState.isSubmitting}
                error={fieldState.invalid || kushki.hasCardProcessErrors}
              />
            )}
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="cardCvv"
            control={control}
            defaultValue={false}
            rules={{ required: true, validate: { cvv: isCVVNumberValid } }}
            render={({ field, fieldState }) => (
              <MaskedInput
                fullWidth
                data-testid="payment-cvv"
                type="password"
                label={t('checkoutScreen.payment.methods.card.cvv')}
                autoComplete="off"
                inputProps={{
                  inputMode: 'numeric',
                  mask: '9999',
                  className: css.cvv,
                }}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                disabled={formState.isSubmitting}
                error={fieldState.invalid || kushki.hasCardProcessErrors}
              />
            )}
          />
        </Grid>

        <Grid item xs={6} hidden={!checkout.isZipCodeRequired}>
          <Input
            fullWidth
            data-testid="payment-zip-code"
            label={t('checkoutScreen.payment.methods.card.zipCode')}
            autoComplete="off"
            inputProps={{ maxLength: 6 }}
            value={kushki.card.zipCode}
            onChange={(e) => {
              kushki.updateField('card.zipCode', e.target.value);
            }}
            disabled={kushki.isProcessing}
            error={kushki.errors.card.zipCode}
          />
        </Grid>
      </Grid>
      <PayButton
        onClick={handleSubmit(pay as SubmitHandler<FieldValues>, onValidationError)}
        disabled={kushki.isProcessing || !checkout.isAllowedToPayWithCard}
        loading={kushki.isProcessing}
        isSubscription={isSubscription}
        label={
          !isSubscription
            ? t('checkoutScreen.payment.methods.submit')
            : t('checkoutScreen.payment.methods.subscribe')
        }
      />
      <FormPartnerLogo partner={Partner.KUSHKI} />
    </>
  );
};

export default observer(KushkiCardForm);
